import { DatePipe, NgIf, NgFor, AsyncPipe, LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatTableDataSource } from '@angular/material/table';
import { OrderTypes } from '@next/core-lib/cart';

import { AbstractDialog, AbstractDialogData, AbstractDialogResultData, AgrDialogModule } from '@next/core-lib/dialog';
import { LocaleService, AgrI18nSharedModule } from '@next/core-lib/i18n';
import { LowercaseFirstPipe } from '@next/core-lib/pipes';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableColumnTypes, TableConfig, AgrTableModule } from '@next/core-lib/table';
import { TableColumn } from '@next/core-lib/table';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { OrderDetails, OrderDetailsColumnTypes, OrderLine } from '../../models/order.models';
import { FormatFlexibilityDatePipe } from '../../product/pipes/format-flexibility-date.pipe';
import { selectOrderDetails } from '../../store/selectors';

@UntilDestroy()
@Component({
  selector: 'app-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LowercaseFirstPipe],
  imports: [AgrDialogModule, NgIf, MatDivider, MatIcon, AgrTableModule, NgFor, AsyncPipe, LowerCasePipe, AgrI18nSharedModule, FormatFlexibilityDatePipe],
})
export class OrderDetailsComponent extends AbstractDialog<AbstractDialogData> implements OnInit {
  @ViewChild('nameColumn') nameColumn: TemplateRef<unknown>;
  @ViewChild('deliveryDateColumn') deliveryDateColumn: TemplateRef<unknown>;
  @ViewChild('orderQuantityColumn') orderQuantityColumn: TemplateRef<unknown>;
  @ViewChild('externalInvoiceIdColumn') externalInvoiceIdColumn: TemplateRef<unknown>;
  @ViewChild('silosColumn') silosColumn: TemplateRef<unknown>;
  @ViewChild('supplementsColumn') supplementsColumn: TemplateRef<unknown>;

  public orderDetails$: Observable<OrderDetails> = this.store.select(selectOrderDetails).pipe(
    tap((orderDetails) => {
      if (orderDetails) {
        this.orderTypePrefixForTranslations = orderDetails?.orderType === OrderTypes.returnableOrder ? 'returnable-' : '';
        this.setTableConfigurations(orderDetails);
        this.setTranslations(orderDetails);
      }
    }),
  );

  public returnableOrderType = OrderTypes.returnableOrder;
  public orderTypePrefixForTranslations = '';
  public description = '';

  public tableConfig: TableConfig = {
    dataSource: new MatTableDataSource(),
    header: true,
    footer: false,
    scrolling: false,
    disableCards: false,
    selectColumn: false,
    pagination: false,
    columns: [],
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AbstractDialogData,
    @Inject(BreakpointServiceInjector) readonly breakpoint: BreakpointService,
    protected mdDialogRef: MatDialogRef<OrderDetailsComponent, AbstractDialogResultData<never>>,
    private readonly store: Store,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly localeService: LocaleService,
    private readonly datePipe: DatePipe,
  ) {
    super();
  }

  ngOnInit(): void {
    this.orderDetails$
      .pipe(untilDestroyed(this))
      .pipe(
        map((orderDetails: OrderDetails) => ({
          ...orderDetails,
          orderLines: orderDetails?.orderLines?.map((orderLine) => ({
            ...orderLine,
            deliveryInformation: {
              ...orderLine.deliveryInformation,
              flexibility: orderLine.deliveryInformation?.flexibility,
            },
            name: orderLine.skuDetail?.title || orderLine.skuDetail?.description,
            nameMobile: this.createNameMobileLabel(orderLine),
            silosLabel: orderLine.silos?.map((silo) => silo.siloNumber || `Id: ${silo.id}`).join(', '),
          })),
        })),
      )
      .subscribe((orderDetails) => {
        if (orderDetails?.orderLines) {
          this.tableConfig = {
            ...this.tableConfig,
            dataSource: new MatTableDataSource(orderDetails?.orderLines || []),
          };
        }
        this.changeDetectorRef.detectChanges();
      });
  }

  private setTableConfigurations(orderDetails: OrderDetails) {
    this.breakpoint.ltMd$.pipe(untilDestroyed(this)).subscribe((isMobile) => {
      this.tableConfig = {
        ...this.tableConfig,
        columns: this.createColumns(isMobile, orderDetails?.orderType),
        primaryColumn: isMobile ? OrderDetailsColumnTypes.nameMobile : OrderDetailsColumnTypes.name,
      };

      if (!isMobile) {
        this.tableConfig.columns[0].template = this.nameColumn;
      }

      if (orderDetails?.orderType === OrderTypes.returnableOrder) {
        this.tableConfig.columns[1].template = this.orderQuantityColumn;
        this.tableConfig.columns[2].template = this.externalInvoiceIdColumn;
      } else {
        this.tableConfig.columns[1].template = this.deliveryDateColumn;
        this.tableConfig.columns[2].template = this.orderQuantityColumn;
        this.tableConfig.columns[3].template = this.silosColumn;
        this.tableConfig.columns[4].template = this.supplementsColumn;
      }

      this.changeDetectorRef.detectChanges();
    });
  }

  private createColumns(isMobile: boolean, orderType: OrderTypes): TableColumn[] {
    const orderQuantityColumn: TableColumn = {
      id: OrderDetailsColumnTypes.orderQuantity,
      type: TableColumnTypes.TEMPLATE,
      label: `${this.orderTypePrefixForTranslations}order-details.products.${OrderDetailsColumnTypes.orderQuantity}`,
      template: this.orderQuantityColumn,
    };

    const columns: TableColumn[] = [
      {
        id: isMobile ? OrderDetailsColumnTypes.nameMobile : OrderDetailsColumnTypes.name,
        type: TableColumnTypes.TEMPLATE,
        label: `order-details.products.${isMobile ? OrderDetailsColumnTypes.nameMobile : OrderDetailsColumnTypes.name}`,
        template: isMobile ? null : this.nameColumn,
        className: isMobile ? null : 'flex__child--2',
        headerClassName: isMobile ? null : 'flex__child--2',
      },
    ];

    if (orderType === OrderTypes.returnableOrder) {
      columns.push(orderQuantityColumn);
      columns.push({
        id: OrderDetailsColumnTypes.externalInvoiceId,
        type: TableColumnTypes.TEMPLATE,
        label: `order-details.products.${OrderDetailsColumnTypes.externalInvoiceId}`,
        template: this.externalInvoiceIdColumn,
      });
    } else {
      columns.push(
        {
          id: OrderDetailsColumnTypes.deliveryDate,
          type: TableColumnTypes.TEMPLATE,
          label: `${this.orderTypePrefixForTranslations}order-details.products.${OrderDetailsColumnTypes.deliveryDate}`,
          template: this.deliveryDateColumn,
          className: 'flex__child--2',
          headerClassName: 'flex__child--2',
        },
        orderQuantityColumn,
        {
          id: OrderDetailsColumnTypes.silos,
          type: TableColumnTypes.TEMPLATE,
          label: `order-details.products.${OrderDetailsColumnTypes.silos}`,
          template: this.silosColumn,
        },
        {
          id: OrderDetailsColumnTypes.supplements,
          type: TableColumnTypes.TEMPLATE,
          label: `order-details.products.${OrderDetailsColumnTypes.supplements}`,
          template: this.supplementsColumn,
        },
      );
    }

    return columns;
  }

  private setTranslations(orderDetails: OrderDetails) {
    this.data.title = this.localeService.translate(`${this.orderTypePrefixForTranslations}order-details.title`, {
      date: this.datePipe.transform(orderDetails?.createdOn),
    });

    this.data.subTitle =
      orderDetails?.orderNumbers?.length > 0
        ? this.localeService.translate(`${this.orderTypePrefixForTranslations}order-details.subtitle`, {
            id: orderDetails?.orderNumbers.join(','),
          })
        : null;

    this.description =
      orderDetails?.orderNumbers?.length > 0
        ? this.localeService.translate(`${this.orderTypePrefixForTranslations}order-details.description`, {
            id: orderDetails?.orderNumbers.join(','),
            date: this.datePipe.transform(orderDetails?.createdOn),
          })
        : this.localeService.translate(`${this.orderTypePrefixForTranslations}order-details.description-no-order-numbers`, {
            date: this.datePipe.transform(orderDetails?.createdOn),
          });
  }

  private createNameMobileLabel(orderLine: OrderLine): string {
    let label = orderLine?.skuDetail?.title || orderLine?.skuDetail?.description + '';

    if (orderLine?.skuDetail?.externalId?.length) {
      label += ` (${orderLine.skuDetail.externalId})`;
    }

    return label;
  }
}
